import { computed, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import { useMutation, useQuery, useLazyQuery } from '@vue/apollo-composable';
import type { ApolloCache } from '@apollo/client/core';
import { useToast } from 'primevue/usetoast';
import type {
  GatewaySettlementAccount,
  CreateGatewaySettlementAccountInput,
  UpdateGatewaySettlementAccountInput,
} from '@/types/composition-functions';
import {
  GET_GATEWAY_SETTLEMENT_ACCOUNTS,
  CREATE_GATEWAY_SETTLEMENT_ACCOUNT,
  UPDATE_GATEWAY_SETTLEMENT_ACCOUNT,
  DELETE_GATEWAY_SETTLEMENT_ACCOUNT,
  SET_DEFAULT_GATEWAY_SETTLEMENT_ACCOUNT,
} from '@/graphql/billing/gatewaySettlementAccounts';
import { settlementAccountErrorMessage } from './gatewaySettlementAccountForm';

export type { GatewaySettlementAccount };

/** Root fields that list or group settlement accounts. */
export const GATEWAY_SETTLEMENT_QUERY_FIELDS = ['gatewaySettlementAccounts', 'checkoutGroups', 'checkoutGroupPreview'] as const;

/**
 * Drops every cached copy of the accounts list — the per-provider list in
 * Settings and the all-accounts "Pays into" dropdown on fee screens — plus
 * checkout groupings. Whatever is on screen refetches; screens opened later
 * fetch fresh. Takes only the cache so it can be passed as `update`.
 */
export function evictGatewaySettlementAccounts(cache: ApolloCache<unknown>): void {
  for (const fieldName of GATEWAY_SETTLEMENT_QUERY_FIELDS) {
    cache.evict({ id: 'ROOT_QUERY', fieldName });
  }
  cache.gc();
}

/** Read-only list of one provider's accounts (shares the cache with the panel). */
export function useGatewaySettlementAccountList(paymentProviderId: Ref<string | null>) {
  const queryVars = computed(() => ({
    payment_provider_id: paymentProviderId.value ?? '',
  }));

  const { result, loading, error, refetch } = useQuery<{
    gatewaySettlementAccounts: GatewaySettlementAccount[];
  }>(GET_GATEWAY_SETTLEMENT_ACCOUNTS, queryVars, () => ({
    enabled: !!paymentProviderId.value,
    fetchPolicy: 'cache-and-network',
  }));

  const accounts = computed(() => result.value?.gatewaySettlementAccounts ?? []);
  const loaded = computed(() => !!result.value);

  return { accounts, loading, loaded, error, refetch };
}

export function useGatewaySettlementAccounts(
  paymentProviderId: Ref<string | null>,
  options: { noun?: MaybeRefOrGetter<string> } = {},
) {
  const toast = useToast();
  const noun = () => toValue(options.noun) || 'Pays-into account';

  const { accounts, loading, error, refetch } = useGatewaySettlementAccountList(paymentProviderId);

  const mutationOptions = { update: evictGatewaySettlementAccounts };
  const { mutate: createMutate, loading: isCreating } = useMutation(CREATE_GATEWAY_SETTLEMENT_ACCOUNT, mutationOptions);
  const { mutate: updateMutate, loading: isUpdating } = useMutation(UPDATE_GATEWAY_SETTLEMENT_ACCOUNT, mutationOptions);
  const { mutate: deleteMutate, loading: isDeleting } = useMutation(DELETE_GATEWAY_SETTLEMENT_ACCOUNT, mutationOptions);
  const { mutate: setDefaultMutate, loading: isSettingDefault } = useMutation(SET_DEFAULT_GATEWAY_SETTLEMENT_ACCOUNT, mutationOptions);

  function failed(summary: string, err: unknown) {
    toast.add({ severity: 'error', summary, detail: settlementAccountErrorMessage(err, noun()), life: 7000 });
  }

  /** Throws on failure so the form stays open for corrections. */
  async function createAccount(input: CreateGatewaySettlementAccountInput) {
    try {
      await createMutate({ input });
      toast.add({
        severity: 'success',
        summary: `${noun()} added`,
        detail: `"${input.label}" can now be chosen as the bank account a fee pays into.`,
        life: 4000,
      });
    } catch (err) {
      failed(`Couldn't add ${noun()}`, err);
      throw err;
    }
  }

  /** Throws on failure so the form stays open for corrections. */
  async function updateAccount(input: UpdateGatewaySettlementAccountInput) {
    try {
      await updateMutate({ input });
      toast.add({ severity: 'success', summary: `${noun()} updated`, detail: `Changes to "${input.label ?? 'this account'}" are saved.`, life: 4000 });
    } catch (err) {
      failed(`Couldn't update ${noun()}`, err);
      throw err;
    }
  }

  /** Returns whether it worked; the toast explains a failure. */
  async function deleteAccount(id: string, label = ''): Promise<boolean> {
    try {
      await deleteMutate({ id });
      toast.add({ severity: 'success', summary: `${noun()} removed`, detail: label ? `"${label}" was removed.` : undefined, life: 4000 });
      return true;
    } catch (err) {
      failed(`Couldn't remove ${noun()}`, err);
      return false;
    }
  }

  /** Returns whether it worked; the toast explains a failure. */
  async function setDefault(id: string, label = ''): Promise<boolean> {
    try {
      await setDefaultMutate({ id });
      toast.add({
        severity: 'success',
        summary: `Default ${noun()} changed`,
        detail: label ? `Fees without their own choice now pay into "${label}".` : undefined,
        life: 4000,
      });
      return true;
    } catch (err) {
      failed(`Couldn't change the default ${noun()}`, err);
      return false;
    }
  }

  return {
    accounts,
    loading,
    error,
    refetch,
    createAccount,
    isCreating,
    updateAccount,
    isUpdating,
    deleteAccount,
    isDeleting,
    setDefault,
    isSettingDefault,
  };
}

export function useAllGatewaySettlementAccounts() {
  const { result, loading, load } = useLazyQuery<{
    gatewaySettlementAccounts: GatewaySettlementAccount[];
  }>(GET_GATEWAY_SETTLEMENT_ACCOUNTS, {}, { fetchPolicy: 'cache-and-network' });

  const accounts = computed(() => result.value?.gatewaySettlementAccounts ?? []);

  return { accounts, loading, load };
}
